(() => {
  /** 镜像 Cookie 名称。Shopify 自己的 _tracking_consent 是 HttpOnly，JS 读不到，
   *  因此额外写一份可读的，供 Consent Mode 引导层在首帧同步恢复同意状态。 */
  const MIRROR_COOKIE = 'satellai_consent';
  const MIRROR_MAX_AGE = 60 * 60 * 24 * 365; // 一年

  /**
   * 自定义 Cookie 同意横幅。
   *
   * 职责：
   *   1. 判断是否需要向访客征询（Shopify 记录为 no_interaction 时才展示）
   *   2. 把访客选择写回 Shopify Customer Privacy API（真实来源）
   *   3. 同步写一份可读的镜像 Cookie
   *   4. 把同样的选择翻译成 Google Consent Mode 信号推给 GTM
   *
   * 注意：Shopify 侧的同意字段只能管住 Shopify 自家分析与 App Pixel（Klaviyo、
   * Meta 等）；GTM 容器里的标签只认 gtag('consent', ...)，所以第 4 步不能省。
   */
  class SatellaiCookieBanner {
    constructor(root) {
      this.root = root;
      this.strategy = root.dataset.strategy === 'opt_out' ? 'opt_out' : 'opt_in';
      this.handleClick = this.handleClick.bind(this);
      this.root.addEventListener('click', this.handleClick);
    }

    /** 加载 Shopify 同意 API，仅在访客尚未做过选择时展示横幅。 */
    init() {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) return;
        const privacy = window.Shopify.customerPrivacy;
        if (!privacy) return;

        try {
          // 在不要求事前同意的美国地区，Shopify 可能直接允许处理，
          // 此时 getTrackingConsent() 不一定是 no_interaction。仍应在访客
          // 尚未作出明确选择时展示主题 Banner，提供告知与退出入口。
          if (typeof privacy.currentVisitorConsent === 'function') {
            const consent = privacy.currentVisitorConsent();
            const values = [consent.analytics, consent.marketing, consent.preferences];
            const hasExplicitChoice = values.some((value) => value === 'yes' || value === 'no');
            if (!hasExplicitChoice) this.show();
            return;
          }

          if (privacy.getTrackingConsent() === 'no_interaction') this.show();
        } catch (e) {
          // 读取失败时不展示，避免在 API 异常时反复打扰访客
        }
      });
    }

    show() {
      this.root.hidden = false;
      // 下一帧再加可见类，让 CSS 过渡能正常播放
      requestAnimationFrame(() => this.root.classList.add('is-visible'));
    }

    hide() {
      this.root.classList.remove('is-visible');
      // 等过渡结束再真正移出无障碍树
      window.setTimeout(() => {
        this.root.hidden = true;
      }, 260);
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-satellai-cookie-action]');
      if (!trigger || !this.root.contains(trigger)) return;
      const action = trigger.dataset.satellaiCookieAction;
      if (action === 'accept') this.save(true);
      if (action === 'reject') this.save(false);
    }

    /** 把选择写入三处：Shopify API、镜像 Cookie、Consent Mode。 */
    save(granted) {
      this.writeShopifyConsent(granted);
      this.writeMirrorCookie(granted);
      this.pushConsentMode(granted);
      this.hide();
    }

    writeShopifyConsent(granted) {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.setTrackingConsent !== 'function') return;
      // 用对象形式分类写入，便于后续按类别细化；当前四类取同一个值
      privacy.setTrackingConsent(
        {
          analytics: granted,
          marketing: granted,
          preferences: granted,
          sale_of_data: granted
        },
        () => {}
      );
    }

    writeMirrorCookie(granted) {
      const payload = {
        v: 1,
        s: { a: granted, m: granted, p: granted },
        t: Date.now()
      };
      try {
        const value = encodeURIComponent(JSON.stringify(payload));
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie =
          MIRROR_COOKIE + '=' + value + '; Path=/; Max-Age=' + MIRROR_MAX_AGE + '; SameSite=Lax' + secure;
      } catch (e) {
        // 镜像 Cookie 只是首帧优化，写失败不影响 Shopify 侧的真实记录
      }
    }

    /** GTM 容器里的标签只认 Consent Mode 信号，必须单独推送一次。
     *  注意必须走 gtag 的 arguments 形式（gtag.js 只识别这种结构），
     *  不能直接 push 一个普通数组。 */
    pushConsentMode(granted) {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };
      }
      const value = granted ? 'granted' : 'denied';
      window.gtag('consent', 'update', {
        ad_storage: value,
        ad_user_data: value,
        ad_personalization: value,
        analytics_storage: value,
        functionality_storage: value,
        personalization_storage: value
      });
      // 供 GTM 侧建触发器使用
      window.dataLayer.push({
        event: 'satellai_consent_update',
        consent_granted: granted,
        consent_strategy: this.strategy
      });
    }

    destroy() {
      this.root.removeEventListener('click', this.handleClick);
    }
  }

  const instances = new WeakMap();

  /** 初始化指定范围内的横幅，用 WeakMap 避免重复绑定。 */
  function init(scope = document) {
    const roots = [];
    if (scope.matches && scope.matches('[data-satellai-cookie-banner]')) roots.push(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll('[data-satellai-cookie-banner]').forEach((el) => roots.push(el));
    }
    roots.forEach((root) => {
      if (instances.has(root)) return;
      const banner = new SatellaiCookieBanner(root);
      instances.set(root, banner);
      banner.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!target.querySelectorAll) return;
    target.querySelectorAll('[data-satellai-cookie-banner]').forEach((root) => {
      const banner = instances.get(root);
      if (banner) {
        banner.destroy();
        instances.delete(root);
      }
    });
  });
})();
